import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store/planStore';
import api from '../services/api';

const AuthContext = createContext(null);

const AUTH_URL = 'https://auth.emergentagent.com';

export function AuthProvider({ children }) {
  const { 
    user, 
    sessionToken, 
    loading,
    needsOnboarding,
    setUser, 
    setSessionToken, 
    setLoading,
    setNeedsOnboarding,
    logout: storeLogout,
    completeOnboarding 
  } = useAuthStore();
  
  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      if (sessionToken) {
        setLoading(true);
        try {
          const userData = await api.getCurrentUser();
          setUser(userData);
          
          // Check onboarding status
          if (userData.user_id) {
            try {
              const dnaData = await api.getTravelDNA(userData.user_id);
              setNeedsOnboarding(!dnaData.onboarding_completed);
            } catch {
              setNeedsOnboarding(true);
            }
          }
        } catch (error) {
          console.error('Session check failed:', error);
          storeLogout();
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    checkSession();
    
    // Handle auth callback from URL
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    let sessionId = urlParams.get('session_id');
    if (!sessionId && hash.includes('session_id=')) {
      sessionId = hash.split('session_id=')[1]?.split('&')[0];
    }
    
    if (sessionId) {
      handleGoogleCallback(sessionId);
    }
  }, []);
  
  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const data = await api.googleAuthCallback(sessionId);
      setSessionToken(data.session_token);
      setUser(data.user);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Google auth callback failed:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const signInWithGoogle = useCallback(() => {
    const redirectUrl = `${window.location.origin}/`;
    const authUrl = `${AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  }, []);
  
  const signInWithEmail = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const data = await api.login(email, password);
      setSessionToken(data.session_token || data.user_id);
      setUser({
        user_id: data.user_id,
        email: data.email,
        name: data.profile?.name || email.split('@')[0],
        picture: data.profile?.picture,
      });
      return data;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setSessionToken, setUser]);
  
  const signUpWithEmail = useCallback(async (email, password, name) => {
    setLoading(true);
    try {
      await api.signup(email, password, name);
      // Auto login after signup
      return await signInWithEmail(email, password);
    } finally {
      setLoading(false);
    }
  }, [setLoading, signInWithEmail]);
  
  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      storeLogout();
    }
  }, [storeLogout]);
  
  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    needsOnboarding,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    completeOnboarding,
    setUser,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
